import moment from 'moment';
import t, { partial } from '../../../util/translation/translation';
import {
  TAKEOVER_BODY_MAX,
  TAKEOVER_FACT_MAX,
  TAKEOVER_LINK_MAX,
  TAKEOVER_TEXT_FIELDS,
  TAKEOVER_TEXT_MAX,
  isGenericTakeover,
  isHttpUrl,
  isLightColor,
} from '../takeoverUtils';

const p = partial('validateErrors');
const tooLong = (value, max) => `${value || ''}`.trim().length > max;

// A takeover's own text and style (as the spotlight service checks them).
// The image-and-text layout hides, and doesn't save, what only the
// spotlight shows, so that isn't checked.
const validatePresentation = (presentation) => {
  const errors = {};
  const generic = isGenericTakeover(presentation);
  TAKEOVER_TEXT_FIELDS.forEach((name) => {
    if (generic && name === 'firstLine') return;
    if (tooLong(presentation?.[name], TAKEOVER_TEXT_MAX)) {
      errors[name] = p('takeoverTooLong', { max: TAKEOVER_TEXT_MAX });
    }
  });
  const facts = (generic ? [] : presentation?.facts || []).map((fact) =>
    tooLong(fact, TAKEOVER_FACT_MAX)
      ? p('takeoverTooLong', { max: TAKEOVER_FACT_MAX })
      : undefined
  );
  if (facts.some(Boolean)) {
    errors.facts = facts;
  }
  // The text link needs both its text and where it goes
  const moreLabel = `${presentation?.moreLabel || ''}`.trim();
  const moreLink = `${presentation?.moreLink || ''}`.trim();
  if (moreLink && !isHttpUrl(moreLink)) {
    errors.moreLink = p('takeoverLinkInvalid');
  } else if (moreLink.length > TAKEOVER_LINK_MAX) {
    errors.moreLink = p('takeoverTooLong', { max: TAKEOVER_LINK_MAX });
  } else if (moreLabel && !moreLink) {
    errors.moreLink = p('takeoverLinkMissing');
  }
  if (moreLink && !moreLabel && !errors.moreLabel) {
    errors.moreLabel = p('takeoverLinkLabelMissing');
  }
  if (
    !generic &&
    presentation?.lightColor &&
    !isLightColor(presentation.lightColor)
  ) {
    errors.lightColor = p('colorInvalid');
  }
  return errors;
};

const validateTileForm = (values, test) => {
  const errors = {};
  if (!values.title) {
    errors.title = p('headerFieldCantBeEmpty');
  }

  if (
    !values.headingImage &&
    test.format !== 'TEXT_BANNER' &&
    values.content?.value !== 'YOUTUBE_VIDEO'
  ) {
    errors.headingImage = p('picFieldCantBeEmpty');
  }

  // A takeover shows no description, except the image-and-text one, as
  // its (optional) text
  if (
    test.descriptionAvailable &&
    test.format !== 'TAKEOVER' &&
    !values.description
  ) {
    errors.description = p('descriptionFieldCantBeEmpty');
  }
  if (
    test.format === 'TAKEOVER' &&
    isGenericTakeover(values.presentation) &&
    tooLong(values.description, TAKEOVER_BODY_MAX)
  ) {
    errors.description = p('takeoverTooLong', { max: TAKEOVER_BODY_MAX });
  }
  if (!values.publicationDate) {
    errors.publicationDate = p('publicationDateFieldCantBeEmpty');
  }
  if (!values.content) {
    errors.content = p('fieldEmpty');
  }
  // Image tiles have no target
  if (!values.contentType && values.content?.value !== 'IMAGE') {
    errors.contentType = p('fieldEmpty');
  }
  if (values?.content?.value === 'AUCTIONS' && !values.contentType) {
    errors.contentType = p('fieldEmpty');
  }
  if (
    values?.content?.value === 'AUCTIONS' &&
    values?.contentType?.value !== null &&
    !values.subContentType
  ) {
    errors.subContentType = p('fieldEmpty');
  }
  if (
    values.content?.value === 'EXTERNAL_LINK' &&
    !values?.contentType?.includes('http') &&
    !values?.contentType?.includes('mailto:') &&
    !values?.contentType?.includes('mingolf:') &&
    !values?.contentType?.includes('tel:')
  ) {
    errors.contentType = t('CompanyForm.websiteUrlTooltip');
  }
  if (values.content?.value === 'YOUTUBE_VIDEO' && !values?.contentType) {
    errors.contentType = t('NewsForm.youTubeError');
  }
  if (values.content?.value === 'CUSTOM_VIDEO' && !values?.headingVideo) {
    errors.headingVideo = p('videoFieldCantBeEmpty');
  }
  if (values.publicationDate && values.expirationDate) {
    if (
      moment(values.expirationDate).isBefore(moment(values.publicationDate))
    ) {
      errors.expirationDate = p('endBeforeStart');
    }
  }
  if (test.format === 'TAKEOVER') {
    const presentationErrors = validatePresentation(values.presentation);
    if (Object.keys(presentationErrors).length) {
      errors.presentation = presentationErrors;
    }
  }
  return errors;
};
export default validateTileForm;
